'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { trackEvent } from '@/lib/analytics';

/**
 * Tracks scroll depth milestones (25%, 50%, 75%, 100%) per page.
 * Fires each milestone only once per page visit.
 * Resets on route changes.
 */
export function ScrollDepthTracker() {
	const pathname = usePathname();
	const milestonesHit = useRef(new Set<number>());

	useEffect(() => {
		// Reset milestones on route change
		milestonesHit.current = new Set<number>();

		const handleScroll = () => {
			const scrollTop = window.scrollY;
			const docHeight = document.documentElement.scrollHeight - window.innerHeight;

			if (docHeight <= 0) return;

			const scrollPercent = Math.round((scrollTop / docHeight) * 100);

			const milestones = [25, 50, 75, 100];
			for (const milestone of milestones) {
				if (scrollPercent >= milestone && !milestonesHit.current.has(milestone)) {
					milestonesHit.current.add(milestone);
					trackEvent('scroll_depth', {
						depth: milestone,
						page: pathname,
					});
				}
			}
		};

		window.addEventListener('scroll', handleScroll, { passive: true });

		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, [pathname]);

	return null;
}
